import type { FlagsStateContainer } from '@confluence/flags';

import { useLoomService } from './useLoomService';

export type Integrations = 'loom';
/**
 * A mapping of integration name to main integration function
 * and availability check function
 * The main function should accept at least an onSuccess handler
 * The otherProps value is for sending in extra props
 */
type IntegrationMap = Partial<
	Record<
		Integrations,
		{
			integration: ({
				onSuccess,
				onFailure,
				otherProps,
			}: {
				onSuccess: (props: unknown) => void;
				onFailure?: (args: unknown) => void;
				otherProps: any;
			}) => Promise<void>;
			isAvailable: () => boolean;
		}
	>
>;

const integrationsMap: IntegrationMap = {};

export const useIntegrationService = (flags: FlagsStateContainer, contentId: string) => {
	const loomService = useLoomService(flags, contentId);

	integrationsMap['loom'] = {
		integration: loomService.openLoomRecorder,
		isAvailable: loomService.isAvailable,
	};

	const triggerIntegration = ({
		id,
		onSuccess,
		onFailure,
		otherProps,
	}: {
		id: keyof typeof integrationsMap;
		onSuccess: (args: unknown) => void;
		onFailure?: (args: unknown) => void;
		otherProps: unknown;
	}) =>
		integrationsMap[id]
			? integrationsMap[id]!.integration({ onSuccess, onFailure, otherProps })
			: Promise.reject('No integration found');
	const checkIntegration = ({ id }: { id: keyof typeof integrationsMap }) =>
		integrationsMap[id]?.isAvailable() || false;

	return { triggerIntegration, checkIntegration };
};

export type integrationService = ReturnType<typeof useIntegrationService>;
